import { runInAction, reaction, makeAutoObservable } from 'mobx';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import intersection from 'lodash/intersection';
import pick from 'lodash/pick';
import services from '../services';
import Assessment from './models/assessment';
import { extractErrorMessage, arraysEqual } from '../utils/helpers';
import { ASSESSMENT_WIZARD_STEPS, STATUS, ASSESSMENT_TYPES, ASSESSMENT_INSIGHT_WIZARD_STEPS } from '../utils/constants';
import {
    generateCreateEntity,
    generateDeleteEntity,
    generateLoadEntity,
    generateLoadList,
    generateUpdateEntity,
} from '../utils/mobx';
import moment from 'moment';
import PersonalizeFormDef from '../forms/personalize';
import EmailsForm from '../forms/hra-email-setting';
import { translateAssessmentScheduleType } from '../utils/scheduleType';
import { getDeliveryChannels } from '../utils/deliveryChannel';
import React from 'react';
import { Buffer } from 'buffer';
import uniq from 'lodash/uniq';

const activeStatuses = { [STATUS.SCHEDULED]: 1, [STATUS.ACTIVE]: 1 };
const completedStatuses = {
    [STATUS.COMPLETED]: 1,
};

class AssessmentStore {
    assessments = [];
    latestTenAssessments = []; //Light-weight objects for the latest (10) updated assessments
    dashboardAssessments = [];
    loadingAssessments = false;
    loadingAssessmentsInBackground = false;
    loadingDashboardAssessments = false;
    loadingDashboardAssessmentsInBackground = false;
    loadingLatestTenAssessments = false;
    loadingAssessment = false;
    loadingInfosecSummary = false;
    loadingQuestionsReportList = false;
    loadingEmployeesReportList = false;
    loadingThreatAreaDetail = false;
    loadingRiskInsights = false;
    loadingListScoresPerThreatArea = false;
    loadingThreatAreasList = false;
    loadingKeyBehavioursList = false;
    loadingRemindersOverview = false;
    slackAvailable = false;
    slackConnected = false;
    teamsAvailable = false;
    teamsConnected = false;
    loadingCompanyData = false;
    templates = [];
    questions = [];
    employeesReportList = [];
    keyBehavioursList = [];
    confirmedSteps = [];
    creatingAssessment = false;
    updatingAssessment = false;
    deletingAssessment = false;
    sendingPreview = false;
    remindersOverview = [];
    sendError = null;
    assessment = null;
    editingAssessment = null;
    infosecSummary = null;
    threatAreaDetail = [];
    threatAreasList = [];
    riskInsights = [];
    listScoresPerThreatArea = [];
    selectedTemplates = [];
    templateCustomized = false;
    error = '';
    step = ASSESSMENT_WIZARD_STEPS.GOAL;
    searchValue = '';
    areaFilter = [];
    employeeFilter = [];
    startDueDateFilter = null;
    endDueDateFilter = null;
    endDeliveryDateFilter = null;
    startDeliveryDateFilter = null;
    sortBy = '_rawData.last_updated';
    platform = 'all_platforms';
    personalizeForm = null;
    threatAreaDefinitions = [];
    loadingThreatAreaDefinitions = null;
    isCSVDownloading = false;
    dueError = null;

    //By user
    ASSESSMENT_STEPS_CONSTANT = ASSESSMENT_WIZARD_STEPS;
    
    userAssessments = [];
    loadingUserAssessments = false;
    isAssessmentInsight = false;
    emailsForm = null;
    visitedSteps        = [];

    constructor(
        listStore,
        mainStore,
        commonStore,
        companyStore,
        authStore,
        brandingStore,
        genericContext
    ) {
        makeAutoObservable(this);
        this.listStore = listStore;
        this.mainStore = mainStore;
        this.commonStore = commonStore;
        this.companyStore = companyStore;
        this.authStore = authStore;
        this.brandingStore = brandingStore;
        this.genericContext = genericContext;
        this.isAssessmentInsight = this.genericContext.type === ASSESSMENT_TYPES.ASSESSMENT_INSIGHT;
        this.ASSESSMENT_STEPS_CONSTANT = this.isAssessmentInsight ? ASSESSMENT_INSIGHT_WIZARD_STEPS : ASSESSMENT_WIZARD_STEPS;
        this.initStepValidators();
        this.step = this.ASSESSMENT_STEPS_CONSTANT.GOAL;


        reaction(
            () => this.mainStore.currentCompany,
            (currentCompany) => {
                if (currentCompany) {
                    this.clearFilters();
                }
                // Clear list data and loading state so list view can load after company switch
                runInAction(() => {
                    this.assessments.replace([]);
                    this.latestTenAssessments.replace([]);
                    this.dashboardAssessments.replace([]);
                    this.loadingAssessments = false;
                    this.loadingAssessmentsInBackground = false;
                    this.loadingDashboardAssessments = false;
                    this.loadingDashboardAssessmentsInBackground = false;
                    this.loadingLatestTenAssessments = false;
                });
            }
        );
        reaction(
            () => this.assessment ? this.assessment.personalizedKeys.slice() : [],

            (keys, prev) => {
                if (!arraysEqual(keys, prev)) this.personalizeForm = null;
                this.updatePersonalizeForm();
            }
        );

    }

    setError(error, type) {
        switch (type) {
            case 'updateDue':
                this.dueError = error;
                break;
            case 'sendPreview':
                this.sendError = error;
                break;
            default:
                this.error = error;
                break;
        }
    }

    get filteredAssessments() {
        let orderedAssessments = orderBy(
            this.assessments,
            [this.sortBy],
            ['desc']
        );

        let filteredAssessments = orderedAssessments.filter((x) => {
            if (
                this.areaFilter.length > 0 &&
                intersection(x._rawData.definition.areas, this.areaFilter)
                    .length === 0
            )
                return false;
            if (
                this.employeeFilter.length > 0 &&
                intersection(
                    x._rawData.definition.userLists,
                    this.employeeFilter
                ).length === 0
            )
                return false;
            if (
                this.searchValue.length > 0 &&
                x.name !== null &&
                !x.name.toLowerCase().includes(this.searchValue.toLowerCase())
            )
                return false;
            if (
                this.startDueDateFilter !== null &&
                this.endDueDateFilter !== null &&
                !moment(x.due).isBetween(
                    this.startDueDateFilter,
                    this.endDueDateFilter
                )
            )
                return false;
            if (
                this.startDeliveryDateFilter !== null &&
                this.endDeliveryDateFilter !== null &&
                !moment(x.deliveryDate).isBetween(
                    this.startDeliveryDateFilter,
                    this.endDeliveryDateFilter
                )
            )
                return false;
            if (x.isScorm) return false;
            return true;
        });
        return filteredAssessments;
    }

    get filters () {
        return [
            {
                label: 'Threat areas',
                value: this.areaFilter,
                onChange: (value) => {
                    this.setAreaFilter(value);
                },
                options: Object.values(this.threatAreaDefinitions)
                    .map((area) => ({ value: area.name, label: area.name }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
            },
            {
                label: 'Employee lists',
                value: this.employeeFilter,
                onChange: (value) => {
                    this.setEmployeeFilter(value);
                },
                options: this.isAssessmentInsight ? null : this.listStore.listsWithUsers
                    .map((list) => ({ value: list.id, label: list.name }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
            },
            {
                type: 'date',
                label: `${this.isAssessmentInsight ? 'Created' : 'Delivery'} date`,
                startValue: this.startDeliveryDateFilter,
                endValue: this.endDeliveryDateFilter,
                onStartChange: (value) => {
                    this.setStartDeliveryDateFilter(
                        value ? value.set({ h: 0, m: 0 }) : value
                    );
                },
                onEndChange: (value) => {
                    this.setEndDeliveryDateFilter(
                        value ? value.set({ h: 23, m: 59 }) : value
                    );
                },
            },
            {
                type: 'date',
                label: 'Due date',
                startValue: this.startDueDateFilter,
                endValue: this.endDueDateFilter,
                onStartChange: (value) => {
                    this.setStartDueDateFilter(
                        value ? value.set({ h: 0, m: 0 }) : value
                    );
                },
                onEndChange: (value) => {
                    this.setEndDueDateFilter(
                        value ? value.set({ h: 23, m: 59 }) : value
                    );
                },
            },
        ]
    }

    get isBothAssessmentTypes() {
        return (
            this.assessments.filter((x) => x.isScorm).length > 0 &&
            this.assessments.filter((x) => !x.isScorm).length > 0
        );
    }

    get isScormOnly() {
        return (
            this.assessments.filter((x) => x.isScorm).length > 0 &&
            this.assessments.filter((x) => !x.isScorm).length === 0
        );
    }

    get scormAssessments() {
        return this.assessments.filter((x) => x.isScorm);
    }

    get filteredScormAssessments() {
        return this.scormAssessments.filter((x) => {
            if (
                this.areaFilter.length > 0 &&
                intersection(x._rawData.definition.areas, this.areaFilter)
                    .length === 0
            )
                return false;
            if (
                this.searchValue.length > 0 &&
                x.name !== null &&
                !x.name.toLowerCase().includes(this.searchValue.toLowerCase())
            )
                return false;
            if (
                this.employeeFilter.length > 0 ||
                this.startDueDateFilter !== null ||
                this.endDueDateFilter !== null ||
                this.startDeliveryDateFilter !== null ||
                this.endDeliveryDateFilter !== null
            )
                return false;
            return true;
        });
    }

    get allTab() {
        return this.filteredAssessments;
    }

    get activeTab() {
        return this.filteredAssessments.filter((x) => {
            return activeStatuses[x.status];
        });
    }

    get completedTab() {
        return this.filteredAssessments.filter((x) => {
            return completedStatuses[x.status];
        });
    }

    get draftTab() {
        return this.filteredAssessments.filter((x) => {
            return x.status === STATUS.DRAFT;
        });
    }

    get summaryOutput() {
        const assessment = this.assessment;
        if (!assessment) return null;

        const output = [];

        output.push({
            name: 'Name',
            detail: assessment.name,
        });

        output.push({
            name: 'Threat areas',
            detail: (
                <div>
                    {[...new Set(assessment.questionsSelected.map(q => q.area))].map((area, j, arr) => (
                        <span key={j}>
                        {area}
                            {j !== arr.length - 1 ? ', ' : ''}
                    </span>
                    ))}
                </div>
            ),
        });

        if(assessment.isAssessmentInsight) {
            output.push({
                name: 'Due date',
                detail:
                    assessment.due !== null
                        ? moment(assessment.due).format(`L (LT) [${this.authStore.timeZone}]`)
                        : '—',
            });
            output.push({
                name: 'Authentication type',
                detail: assessment.summaryAuthTypeText
            })
            if(assessment.emails_enabled && this.emailsForm && this.emailsForm.values().users.length > 0) {
                output.push({
                    name: 'Notification emails',
                    detail: uniq(this.emailsForm.values().users.filter(
                        (user) => user.email
                    )).map((user => user.email)).join(', ')
                })
            }

            if(assessment.customization_enabled) {
                output.push({
                    name: 'Banner image URL',
                    detail: assessment.bannerImageSrc
                })
                output.push({
                    name: 'Banner link URL',
                    detail: assessment.bannerLinkURL
                })
                output.push({
                    name: 'Finish URL',
                    detail: assessment.finishUrl
                })
            }
        }

        if (!assessment.isScorm && !assessment.isAssessmentInsight) {
            output.push({
                name: 'Delivery date',
                detail: assessment.summaryDeliveryDateText,
            });

            output.push({
                name: 'Due date',
                detail:
                    assessment.due !== null
                        ? moment(assessment.due).format(`L (LT) [${this.authStore.timeZone}]`)
                        : '—',
            });

            output.push({
                name: 'Reminders',
                detail: assessment.reminders_enabled
                    ? assessment.reminders_steps.filter((reminder) => reminder !== null).length
                    : '—',
            });

            output.push({
                name: `Employees (${assessment.selectedUserCount})`,
                detail: (
                    <div>
                        {assessment.userListsSelected &&
                            assessment.userListsSelected.map((list, j, arr) => (
                                <span key={j}>
                                {list && list.name}
                                    {j !== arr.length - 1 ? ', ' : ''}
                            </span>
                            ))}
                    </div>
                ),
            });
        }

        return output;
    }

    get steps() {
        const steps = [
            {
                number: this.ASSESSMENT_STEPS_CONSTANT.GOAL,
                title:
                    this.assessment?.status === STATUS.ACTIVE
                        ? 'Template'
                        : 'Choose template',
            },
            {
                number: this.ASSESSMENT_STEPS_CONSTANT.CONTENT,
                title: 'Review Scenarios',
            },
            {
                number: this.ASSESSMENT_STEPS_CONSTANT.PERSONALIZE,
                title: 'Personalize',
            },
        ];

        if (this.assessment?.isScorm) {
            steps.push({
                number: this.ASSESSMENT_STEPS_CONSTANT.LANGUAGES,
                title: 'Available Languages',
            });
        } else if (this.assessment?.isAssessmentInsight) {
            steps.push({
                number: this.ASSESSMENT_STEPS_CONSTANT.SETTINGS,
                title: 'Settings',
            });
        } else {
            steps.push(
                {
                    number: this.ASSESSMENT_STEPS_CONSTANT.EMPLOYEES,
                    title: 'Select Employees',
                },
                {
                    number: this.ASSESSMENT_STEPS_CONSTANT.SCHEDULE,
                    title: 'Schedule',
                },
                {
                    number: this.ASSESSMENT_STEPS_CONSTANT.REMINDERS,
                    title: 'Reminders',
                }
            );
        }

        steps.push({
            number: this.ASSESSMENT_STEPS_CONSTANT.FINALIZE,
            title: 'Summary',
        });

        return steps;
    }


    get sortedListScoresPerThreatArea() {
        return this.listScoresPerThreatArea
            .slice()
            .sort((a, b) => b.usersCount - a.usersCount);
    }

    clearFilters() {
        this.setAreaFilter([]);
        this.setEmployeeFilter([]);
        this.setStartDueDateFilter(null);
        this.setEndDueDateFilter(null);
        this.setStartDeliveryDateFilter(null);
        this.setEndDeliveryDateFilter(null);
    }

    setAreaFilter(areaFilter) {
        this.areaFilter = areaFilter;
    }

    setSearchValue(searchValue) {
        this.searchValue = searchValue;
    }

    setThreatAreaFilter = (area) => {
        this.threatAreaFilter = area;
    };

    setEmployeeFilter(employeeFilter) {
        this.employeeFilter = employeeFilter;
    }

    setStartDueDateFilter(startDueDateFilter) {
        this.startDueDateFilter = startDueDateFilter;
    }

    setEndDueDateFilter(endDueDateFilter) {
        this.endDueDateFilter = endDueDateFilter;
    }

    setStartDeliveryDateFilter(startDeliveryDateFilter) {
        this.startDeliveryDateFilter = startDeliveryDateFilter;
    }

    setEndDeliveryDateFilter(endDeliveryDateFilter) {
        this.endDeliveryDateFilter = endDeliveryDateFilter;
    }

    setSortBy(sortBy) {
        this.sortBy = sortBy;
    }

    setStep(step) {
        if (!this.allowedSteps.includes(step)) return;
        this.error = '';
        this.step = step;
        this.markVisited(step);
        this.commonStore.analyticsEvent(
            'Assessment-wizard',
            'step',
            `step-${step}`
        );
    }
    isStepValid(step) {
        const fn = this.stepValidators[step];
        return typeof fn === 'function' ? fn() : false;
    }

    initStepValidators() {
        const C = this.ASSESSMENT_STEPS_CONSTANT;

        this.stepValidators = {
            [C.GOAL]        : () => this.goalValid,
            [C.CONTENT]     : () => this.contentValid,
            [C.PERSONALIZE] : () => this.personalizeValid,
            [C.FINALIZE]    : () => false,
            [C.START]       : () => false,
        };

        if (this.isAssessmentInsight) {
            this.stepValidators[C.SETTINGS] = () => this.settingsValid;
        } else {
            Object.assign(this.stepValidators, {
                [C.EMPLOYEES] : () => this.employeesValid,
                [C.SCHEDULE]  : () => this.scheduleValid,
                [C.REMINDERS] : () => this.remindersValid,
                [C.LANGUAGES] : () => this.languagesValid,
            });
        }
    }

    get allowedSteps() {
        const C = this.ASSESSMENT_STEPS_CONSTANT;
        const steps = [C.GOAL];

        if (this.goalValid) steps.push(C.CONTENT);

        if (this.contentValid) steps.push(C.PERSONALIZE);

        if (this.isScorm) {
            if (this.contentValid) steps.push(C.LANGUAGES);

        } else if (this.isAssessmentInsight) {
            if (
                steps.includes(C.PERSONALIZE) &&
                this.personalizeValid
            ) {
                steps.push(C.SETTINGS);
            }
        } else {
            if (
                steps.includes(C.PERSONALIZE) &&
                this.personalizeValid
            ) {
                steps.push(C.EMPLOYEES);
            }

            if (
                steps.includes(C.EMPLOYEES) &&
                this.employeesValid
            ) {
                steps.push(C.SCHEDULE);
            }

            if (
                steps.includes(C.SCHEDULE) &&
                this.scheduleValid
            ) {
                steps.push(C.REMINDERS);
            }
        }

        if (this.wizardValid) steps.push(C.FINALIZE);

        if (steps.includes(C.FINALIZE) && !steps.includes(C.START)) {
            steps.push(C.START);
        }

        this.visitedSteps.forEach(v => {
            if (!steps.includes(v) && v !== C.FINALIZE && v !== C.START) {
                steps.push(v);
            }
        });

        return steps;
    }


    markVisited(step) {
        if (!this.visitedSteps.includes(step) && step !== this.ASSESSMENT_STEPS_CONSTANT.FINALIZE && step !== this.ASSESSMENT_STEPS_CONSTANT.START) this.visitedSteps.push(step);
    }

    get nextStepNumber() {
        let index = this.steps.findIndex(
            (x) => x.number === this.step
        );
        return this.steps[index + 1]
            ? this.steps[index + 1].number
            : this.step + 1;
    }

    setTemplate(templateId) {
        this.selectedTemplates = [templateId];

        let template = this.templates.find((x) => x.id === templateId);

        if (template) {
            // in case we ever decide to enable selecting multiple templates at the same time
            // if (this.selectedTemplates.includes(templateId)) {
            //     this.selectedTemplates.splice(this.selectedTemplates.indexOf(templateId), 1)
            //     this.assessment.selectedQuestionsIds.replace(difference(this.assessment.selectedQuestionsIds, template.questions))
            // } else {
            this.assessment.templateCustomized = false;
            this.assessment.selectedQuestionsIds.replace(
                template.questions.concat(template.disabledQuestions)
            );
            // }
        }
    }

    get goalValid() {
        return this.selectedTemplates.length > 0;
    }

    get contentValid() {
        return this.assessment?.selectedQuestionsIds.length > 0;
    }

    get personalizeValid() {
        return (
            !this.assessment?.personalizeEnabled ||
            (this.personalizeForm && this.personalizeForm.isValid)
        );
    }

    get employeesValid() {
        return this.assessment?.selectedUserCount > 0;
    }

    get scheduleValid() {
        const a = this.assessment;
        if (!a) return false;

        return (
            a.due &&
            moment(a.due).isAfter(new Date()) &&
            (a.scheduleType === -1 ||
                a.scheduleType === 1 ||
                (a.scheduleType === 0 &&
                    a.date &&
                    moment(a.due).isAfter(a.date))) &&
            a.channel !== 0
        );
    }

    get remindersValid() {
        const a = this.assessment;
        if (!a) return false;
        if (!a.reminders_enabled) return true;

        const allOk = a.reminders_steps.every((r) => {
            if (!r.send) return false;

            const sendMoment = moment(r.send);
            const startRef = a.scheduleType === 0 ? a.date : moment();
            // Reminders already in the past (sent) can't be changed — don't require them to be after new start
            const inPast = sendMoment.isBefore(moment(), 'minute');
            const afterStart =
                inPast || sendMoment.isAfter(startRef, 'minute');

            return afterStart && sendMoment.isBefore(a.due);
        });

        return (
            allOk &&
            a.reminders_steps.length > 0 &&
            a.reminders_channel !== 0
        );
    }

    get languagesValid() {
        return true;
    }

    get settingsValid() {
        if (!this.isAssessmentInsight) return true;

        const a = this.assessment;
        if (!a) return false;

        const emailsOk =
            !a.emails_enabled ||
            (this.emailsForm &&
                this.emailsForm.isValid &&
                this.emailsForm.values().users.length > 0);

        const httpsOk = (v) => !v || v.startsWith('https://');

        const linkImpliesImage =
            !a.bannerLinkURL || (a.bannerImageSrc && httpsOk(a.bannerImageSrc));

        const bannerOk =
            !a.customization_enabled ||
            (
                httpsOk(a.bannerImageSrc) &&
                httpsOk(a.bannerLinkURL) &&
                httpsOk(a.finishUrl) &&
                linkImpliesImage
            );

        return (
            a.due &&
            moment(a.due).isAfter(new Date()) &&
            a.authentication &&
            emailsOk &&
            bannerOk
        );
    }

    get wizardValid() {
        if (this.isScorm) {
            return this.contentValid && this.personalizeValid && this.languagesValid;
        }

        if (this.isAssessmentInsight) {
            return (
                this.settingsValid &&
                this.personalizeValid &&
                this.contentValid
            );
        }

        return (
            this.goalValid        &&
            this.contentValid     &&
            this.personalizeValid &&
            this.employeesValid   &&
            this.scheduleValid    &&
            this.remindersValid
        );
    }

    get saveButtonAvailable() {
        if (this.isScorm || !this.assessment) return false;

        const isDraftOrNew =
            this.assessment.status === STATUS.DRAFT || this.assessment.id === null;

        return isDraftOrNew || this.wizardValid;
    }


    setTemplateAsCustom() {
        const customTemplate = this.templates.find((x) => x.custom);
        if (customTemplate) this.selectedTemplates = [customTemplate.id];
    }


    sendPreview = generateCreateEntity(
        'sendPreview',
        this,
        'sendingPreview',
        async (companyId, targetEmail, intro) => {
            const data = { ...intro, targetEmail };

            data.previewQuestions = this.assessment.selectedQuestionsIds;
            data.name = this.assessment.name;
            data.due = this.assessment.due;
            data.duration =
                this.assessment.overrideDuration ||
                this.assessment.selectedQuestionsDuration;

            await services.Companies.assessmentsService(companyId).sendPreview(
                data
            );
        },
        null,
        {
            onCreated: (result, companyId) => {
                this.commonStore.analyticsEvent(
                    'Assessment-wizard',
                    'preview-sent',
                    `company-${companyId}`
                );
            },
        }
    );

    loadRemindersOverview = generateLoadList(
        'remindersOverview',
        this,
        'loadingRemindersOverview',
        (companyId, assessmentId) => {
            return services.Companies.assessmentsService(
                companyId
            ).remindersOverview(assessmentId);
        },
        'remindersOverview'
    );

    async prepareAssessmentInsightWizard(companyId, copyFrom, editId, enabledLanguages) {
        if (this.loadingCompanyData) return;
        this.loadingCompanyData = true;
        this.error = null;
        this.assessments.clear();
        this.assessment = null;
        this.emailsForm = null;
        this.selectedTemplates = [];
        this.visitedSteps = [];
        try {
            let questions = await services.Companies.questionsService(
                companyId
            ).list();
            let templates = await services.Companies.templatesService(
                companyId
            ).list({ type: 'assessment' });
            runInAction(async () => {
                this.templates.replace(templates);
                this.questions.replace(questions);

                await this.listStore.loadCompanyLists(companyId);

                let fromData;

                if (copyFrom) {
                    let fromAssessment =
                        await services.Companies.assessmentsService(
                            companyId
                        ).fetch(copyFrom);

                    fromData = {
                        ...pick(
                            fromAssessment,
                            'templateIds',
                            'template_customized',
                            'definition',
                        ),
                        name: fromAssessment.name + ' Copy',
                        due: null,
                        id: null,
                    };

                    this.selectedTemplates = fromAssessment.templateIds;
                } else if (editId) {
                    fromData = await services.Companies.assessmentsService(
                        companyId
                    ).fetch(editId);

                    this.selectedTemplates = fromData.templateIds;
                }

                this.assessment = await new Assessment(
                    this.listStore,
                    this.authStore,
                    this.mainStore,
                    this.questionsIndex,
                    fromData,
                    companyId,
                    enabledLanguages,
                    true
                )

                this.setStep(ASSESSMENT_INSIGHT_WIZARD_STEPS.GOAL);

                this.visitedSteps = [...this.allowedSteps.filter((x) => x !== this.ASSESSMENT_STEPS_CONSTANT.FINALIZE && x !== this.ASSESSMENT_STEPS_CONSTANT.START)];

                this.initEmailsForm();
            });
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
        } finally {
            runInAction(() => {
                this.loadingCompanyData = false;
            });
        }
    }
    async prepareWizard(companyId, copyFrom, editId, isScorm, enabledLanguages) {
        if (this.loadingCompanyData) return;
        this.loadingCompanyData = true;
        this.isScorm = isScorm;
        this.error = null;
        this.assessments.clear();
        this.assessment = null;
        this.selectedTemplates = [];
        this.visitedSteps = [];
        try {
            let questions = await services.Companies.questionsService(
                companyId
            ).list();
            let templates = await services.Companies.templatesService(
                companyId
            ).list({ type: 'assessment' });
            runInAction(async () => {
                this.templates.replace(templates);
                this.questions.replace(questions);

                await this.listStore.loadCompanyLists(companyId);
                await this.companyStore.loadIntegrationData(companyId, 'slack');
                await this.companyStore.loadIntegrationData(companyId, 'teams');
                let fromData;

                this.slackConnected =
                    this.companyStore.slackDataIndex[companyId] &&
                    this.companyStore.slackDataIndex[companyId].connected;
                this.teamsConnected =
                    this.companyStore.teamsDataIndex[companyId] &&
                    this.companyStore.teamsDataIndex[companyId].connected &&
                    this.companyStore.teamsDataIndex[companyId].enabled === '1';

                if (copyFrom) {
                    const src = await services.Companies.assessmentsService(
                        companyId
                    ).fetch(copyFrom);

                    fromData = {
                        ...pick(
                            src,
                            'templateIds',
                            'template_customized',
                            'sendOptions.channel',
                            'definition',
                            'sendOptions.scheduleType'
                        ),
                        name: src.name + ' Copy',
                        due : null,
                        id  : null,
                    };
                    this.selectedTemplates = src.templateIds;
                }

                else if (editId) {
                    fromData = await services.Companies.assessmentsService(
                        companyId
                    ).fetch(editId);

                    this.selectedTemplates = fromData.templateIds;
                }

                this.assessment = new Assessment(
                    this.listStore,
                    this.authStore,
                    this.mainStore,
                    this.questionsIndex,
                    fromData,
                    companyId,
                    enabledLanguages
                );
                this.assessment.isScorm = isScorm;

                this.setStep(this.ASSESSMENT_STEPS_CONSTANT.GOAL)

                this.visitedSteps = [...this.allowedSteps.filter((x) => x !== this.ASSESSMENT_STEPS_CONSTANT.FINALIZE && x !== this.ASSESSMENT_STEPS_CONSTANT.START)];
            });
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
        } finally {
            runInAction(() => {
                this.loadingCompanyData = false;
            });
        }
    }

    loadLatestTenAssessments = generateLoadList(
        'loadingLatestTenAssessments',
        this,
        'loadingLatestTenAssessments',
        (companyId) => {
            return services.Companies.assessmentsService(
                companyId
            ).listLatestTen({...this.genericContext});
        },
        'latestTenAssessments',
        (result, companyId) =>
            result.map(
                (x) =>
                    new Assessment(
                        null,
                        null,
                        this.mainStore,
                        null,
                        x,
                        companyId,
                    )
            )
    );

    // d = show drafts (boolean), p = page number, n = number of rows per page
    loadAssessments = generateLoadList(
        'loadingAssessments',
        this,
        'loadingAssessments',
        (companyId, d, p, n) => {
            return services.Companies.assessmentsService(companyId).list({
                d,
                p,
                n,
                ...this.genericContext,
            });
        },
        'assessments',
        (result, companyId) =>
            result.map(
                (x) =>
                    new Assessment(
                        null,
                        null,
                        this.mainStore,
                        null,
                        x,
                        companyId
                    )
            ),
        { backgroundFlag: 'loadingAssessmentsInBackground' }
    );
    loadDashboardAssessments = generateLoadList(
        'loadingDashboardAssessments',
        this,
        'loadingDashboardAssessments',
        (companyId) =>
            services.Companies.assessmentsService(companyId).list({
                d: false,
                p: 0,
                n: 3,
                ...this.genericContext,
            }),
        'dashboardAssessments',
        (result, companyId) =>
            result.map(
                (x) =>
                    new Assessment(
                        null,
                        null,
                        this.mainStore,
                        null,
                        x,
                        companyId
                    )
            ),
        { backgroundFlag: 'loadingDashboardAssessmentsInBackground' }
    );
    loadUserAssessments = generateLoadList(
        'loadUserAssessments',
        this,
        'loadingUserAssessments',
        (companyId, email) => {
            return (
                email &&
                services.Companies.assessmentsService(
                    companyId
                ).userAssessments(email)
            );
        },
        'userAssessments',
        (result, companyId) =>
            result.map(
                (x) =>
                    new Assessment(
                        null,
                        null,
                        this.mainStore,
                        null,
                        x,
                        companyId
                    )
            )
    );

    get usersLatestAssessment() {
        if (!this.userAssessments || this.userAssessments < 1) return null;

        const ordered = this.userAssessments
            .slice()
            .sort(
                (a, b) =>
                    new Date(b._rawData.completed || 0) -
                    new Date(a._rawData.completed || 0)
            );
        return ordered[0];
    }

    loadAssessment = generateLoadEntity(
        'loadAssessment',
        this,
        'loadingAssessment',
        async (companyId, assessmentId) => {
            await this.listStore.loadCompanyLists(companyId);
            const result = await services.Companies.assessmentsService(
                companyId
            ).fetch(assessmentId);

            this.commonStore.analyticsEvent(
                'Assessment',
                'load',
                `company-${companyId}-automation-${assessmentId}`
            );
            return result;
        },
        'editingAssessment',
        null,
        (result, companyId) => {
            return new Assessment(
                this.listStore,
                this.authStore,
                this.mainStore,
                this.questionsIndex,
                result,
                companyId
            );
        }
    );

    loadRiskInsights = generateLoadList(
        'riskInsights',
        this,
        'loadingRiskInsights',
        (companyId, assessmentId) => {
            return services.Companies.assessmentsService(
                companyId
            ).riskInsights(assessmentId);
        },
        'riskInsights'
    );

    loadListScoresPerThreatArea = generateLoadList(
        'listScoresPerThreatArea',
        this,
        'loadingListScoresPerThreatArea',
        (companyId, assessmentId) => {
            return services.Companies.assessmentsService(
                companyId
            ).listScoresPerThreatArea(assessmentId);
        },
        'listScoresPerThreatArea'
    );

    updateSummary(data) {
        this.infosecSummary = data;
    }

    loadInfosecSummary = generateLoadEntity(
        'infosecSummary',
        this,
        'loadingInfosecSummary',
        () => {
            return services.Infosec.summary();
        },
        'infosecSummary'
    );
    loadThreatAreaDefinitions = generateLoadList(
        'threatAreaDefinitions',
        this,
        'loadingThreatAreaDefinitions',
        (companyId) => {
            return services.Companies.assessmentsService(
                companyId
            ).getThreatAreaDefinitions();
        },
        'threatAreaDefinitions',
        null,
        { shouldLoad: () => this.threatAreaDefinitions.length === 0 }
    );

    loadThreatAreaDetail = generateLoadEntity(
        'threatAreaDetail',
        this,
        'loadingThreatAreaDetail',
        (companyId, assessmentId, area, listId) => {
            return services.Companies.assessmentsService(
                companyId
            ).threatAreaDetail(assessmentId, area, listId);
        },
        'threatAreaDetail'
    );

    loadAssessmentEmployeesReport = generateLoadList(
        'employeesReport',
        this,
        'loadingEmployeesReportList',
        (companyId, assessmentId) => {
            return services.Companies.assessmentsService(
                companyId
            ).employeesReportList(assessmentId);
        },
        'employeesReportList'
    );

    loadAssessmentQuestionsReport = generateLoadList(
        'questionsReportList',
        this,
        'loadingQuestionsReportList',
        (companyId, assessmentId, filters) => {
            return services.Companies.assessmentsService(
                companyId
            ).questionsReportList(assessmentId, filters);
        },
        'questionsReportList'
    );

    loadThreatAreasList = generateLoadList(
        'threatAreasList',
        this,
        'loadingThreatAreasList',
        (companyId, assessmentId, listId) => {
            return services.Companies.assessmentsService(
                companyId
            ).threatAreasList(assessmentId, {
                listId,
            });
        },
        'threatAreasList'
    );
    loadKeyBehavioursList = generateLoadList(
        'keyBehavioursList',
        this,
        'loadingKeyBehavioursList',
        (companyId, assessmentId, threatArea, listId, riskScoreBase) => {
            return services.Companies.assessmentsService(
                companyId
            ).keyBehavioursList(assessmentId, {
                threatArea,
                listId,
                riskScoreBase,
            });
        },
        'keyBehavioursList'
    );

    delete = generateDeleteEntity(
        'deleteAssessment',
        this,
        'deletingAssessment',
        async (companyId, assessmentId) => {
            await services.Companies.assessmentsService(companyId).delete(
                assessmentId
            );
            this.editingAssessment = null;
            this.assessment = null;
            this.assessments.replace(
                this.assessments.filter((x) => x.id !== assessmentId)
            );
            return true;
        }
    );

    async sendAssessmentCreatedEvent(assessmentId) {
        const today = new Date();
        const selectedQuestionIds = [...this.assessment.selectedQuestionsIds];
        const selectedQuestions = this.questions.filter((q) =>
            selectedQuestionIds.includes(q.id)
        );
        const threatAreas = new Set(selectedQuestions.map((q) => q.area));

        await this.commonStore.saveAnalyticsEvent('assessment', 'created', {
            assessmentId,
            questionSetName: (() => {
                const selected =
                    this.selectedTemplates.length > 0
                        ? this.selectedTemplates[0]
                        : null;
                if (!selected) return '';
                const template = this.templates.find((t) => t.id === selected);
                return template ? template.title : '';
            })(),
            questionSetId:
                this.selectedTemplates.length > 0
                    ? `${this.selectedTemplates[0]}`
                    : '',
            totalQuestions: selectedQuestionIds.length,
            questionIds: selectedQuestionIds,
            totalThreatAreas: threatAreas.size,
            threatAreaNames: Array.from(threatAreas),
            deliveryType: translateAssessmentScheduleType(
                this.assessment.scheduleType
            ),
            deliveryChannel: getDeliveryChannels(this.assessment.channel),
            totalReminders: this.assessment.reminders_steps.filter(
                (r) => r.send !== null
            ).length,
            totalEmployees: this.assessment.selectedUserCount,
            employeeListIds: [...this.assessment.selectedUserListsIds],
            startDate: this.assessment.date
                ? this.assessment.date.toISOString()
                : today.toISOString(),
            endDate: this.assessment.due.toISOString(),
        });
    }

    async assessmentSaver(companyId, extraData, forceStart = false) {
        if ((this.assessment.status == null || this.assessment.status === STATUS.DRAFT) && !forceStart) {
            this.assessment.setActive(false);
        } else {
            this.assessment.setActive(true);
        }

        const dto = this.assessment.toDTO({
            ...extraData,
            templateIds: this.selectedTemplates,
        })

        return await this.saveAssessment(companyId, dto)
    }

    async saveAssessment(companyId, dto) {
        if (this.creatingAssessment) return;
        this.creatingAssessment = true;

        try {
            let result;

            if (this.assessment.id) {
                result = await services.Companies.assessmentsService(
                    companyId
                ).update(dto);
            } else {
                result = await services.Companies.assessmentsService(
                    companyId
                ).create(dto);

                runInAction(() => {
                    if (result.success)
                        this.assessment.setId(result.assessment.id);
                });
                // await intentionally omitted
                if(this.assessment.active) this.sendAssessmentCreatedEvent(result.assessment.id);
            }
            if(this.assessment.active) this.commonStore.analyticsEvent(
                'assessment',
                'create',
                `company-${companyId}`
            );
            return result.assessment;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
        } finally {
            runInAction(() => {
                this.creatingAssessment = false;
            });
        }
    }

    async pauseAssessment(companyId, assessmentId) {
        if (this.updatingAssessment) return;
        this.updatingAssessment = true;

        try {
            await services.Companies.assessmentsService(
                companyId
            ).pauseAssessment(assessmentId);
            runInAction(() => {
                let respectiveAssessment = this.assessments.find(
                    (x) => x.id === assessmentId
                );
                respectiveAssessment.status = STATUS.DRAFT;
            });
            return true;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
        } finally {
            runInAction(() => {
                this.updatingAssessment = false;
            });
        }
    }

    updatePersonalizeForm() {
        if (!this.personalizeForm) this.initPersonalizeForm();
        else
            this.personalizeForm.updateFields(
                this.assessment.personalizeData,
                this.assessment.personalizedKeys
            );
    }

    initPersonalizeForm() {
        let personalizeForm = new PersonalizeFormDef(
            this.assessment.personalizeData,
            this.assessment.personalizedKeys,
            {
                options: {
                    validateOnChange: true,
                    validateOnBlur: true,
                    showErrorsOnChange: true,
                    showErrorsOnBlur:  true,
                },
            }
        );

        reaction(
            () => {
                return this.assessment;
            },
            (assessment) => {
                if (assessment) {
                    let data = this.assessment.personalizeData || {};
                    personalizeForm.update(data);
                    personalizeForm.validate();
                }
            }
        );

        this.personalizeForm = personalizeForm;
    }

    initEmailsForm() {
        const emailsForm = new EmailsForm(
            {},
            {
                options: {
                    validateOnChange: true,
                    validateOnBlur: true,
                    showErrorsOnChange: true,
                    showErrorsOnBlur:  true,
                },
            }
        );

        if(this.assessment.emailsToNotify.length > 0) {
            emailsForm.update({
                users: this.assessment.emailsToNotify,
            });
        }
        else {
            emailsForm.$('users').onAdd({
                preventDefault: () => {},
            });
        }

        emailsForm.validate();

        this.emailsForm = emailsForm;
    }

    get questionsIndex() {
        return keyBy(this.questions, 'question_id');
    }

    get riskInsightsSorted() {
        return this.riskInsights
            .map((risk) => {
                const number = () => {
                    if (risk.type === 'THREAT_AREA') return 0;
                    if (risk.type === 'KEY_BEHAVIOUR') return 1;
                    if (risk.type === 'EMPLOYEE_LIST') return 2;
                    if (risk.type === 'QUESTIONS') return 3;
                };
                return {
                    ...risk,
                    number: number(),
                };
            })
            .sort((a, b) => a.number - b.number);
    }

    get threatAreaDetailChartValues() {
        return (
            this.editingAssessment &&
            this.threatAreaDetail &&
            Object.values(this.threatAreaDetail.values).map((x) => ({
                ...x,
                employeePercent: (
                    (x.value / this.editingAssessment._rawData.usersFinished) *
                    100
                ).toFixed(1),
            }))
        );
    }

    get maxByAreaAndBehaviourForAllQuestions() {
        let maxByAreaAndBehaviour = {};
        this.questions.forEach((q) => {
            if (q.maxScoresByBehaviour) {
                Object.keys(q.maxScoresByBehaviour).forEach((b) => {
                    if (!maxByAreaAndBehaviour[`${q.area}-${b}`])
                        maxByAreaAndBehaviour[`${q.area}-${b}`] = 0;
                    maxByAreaAndBehaviour[`${q.area}-${b}`] +=
                        q.maxScoresByBehaviour[b];
                });
            }
        });
        return maxByAreaAndBehaviour;
    }

    async downloadCSV(companyId, assessmentId) {
        if (this.isCSVDownloading) return true;
        this.isCSVDownloading = true;

        try {
            const options = {
                responseType: 'blob',
            };

            let response;

            response = await services.Companies.assessmentsService(
                companyId
            ).answersReport(
                assessmentId,
                {
                    csv: true,
                },
                options
            );

            const url = window.URL.createObjectURL(
                new Blob([response.data], {
                    type: response.headers['content-type'],
                })
            );

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'answers.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } finally {
            this.isCSVDownloading = false;
        }
    }

    async rename(companyId, assessmentId, data) {
        if (this.updatingAssessment) return;
        this.updatingAssessment = true;
        try {
            await services.Companies.assessmentsService(
                companyId
            ).renameAssessment(assessmentId, data);
            this.editingAssessment.name = data.name;
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.updatingAssessment = false;
        }
    }

    updateDue = generateUpdateEntity(
        'updateDue',
        this,
        'updatingDue',
        async (companyId, assessmentId, due) => {
            await services.Companies.assessmentsService(companyId).updateDue(
                assessmentId,
                { due }
            );
            return true;
        }
    );

    get demoAssessmentURL() {
        if (!this.mainStore.currentCompany || !this.authStore.currentUser)
            return null;
        const reportTo = `"${this.authStore.currentUser.name}" <${this.authStore.currentUser.email}>`;
        return `${this.brandingStore.myDomain}/assessment/try-demo/${
            this.mainStore.currentCompany.company_uid
        }/${Buffer.from(reportTo, 'utf8').toString('base64')}`;
    }

    setEnabledLanguages(languages) {
        this.assessment.enabledLanguages = languages;
    }
}

export default AssessmentStore;
